import { Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, forkJoin, map } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { ProcessingManager } from '../../../../managers/processing.manager';
import { ProcessingStep } from '../../../../models/ui/processing-step.model';
import { DateUtility } from '../../../../utilities/date.utility';
import { TableLikeBaseComponent } from '../../../table-like-base.component';
import { ActivatedRoute } from '@angular/router';
import { NotificationManager } from '../../../../managers/notification.manager';
import { cloneDeep } from 'lodash-es';

@Component({
    selector: 'lex-all-steps',
    templateUrl: './snapshot-table.component.html',
    styleUrls: ['./snapshot-table.component.scss'],
    standalone: false
})
export class SnapshotTableComponent extends TableLikeBaseComponent<ProcessingStep> implements OnDestroy {
	public selectedIds = [];
	public total: number;
	public skip$ = new BehaviorSubject<number>(0);
	public take$ = new BehaviorSubject<number>(25);
	private functionStepMap = new Map<string, string>();
	private allowedToFailIds = [
		'BAE784BB-4169-456B-B9B0-19A00D5727B3',
		'E5DFFBFA-06DE-4F9E-B461-34A77DFA1C99',
		'17F68D68-BBC9-46A2-AC4D-F90E309DEA61',
		'ECC91DEC-1A7F-4874-A23B-12FEC261B8EE',
		'C96C63B7-9264-4BC6-AC3B-385A191A6278',
		'412310D6-B26B-409A-850B-0BEE7332B568',
		'F9868977-B4C3-486A-859E-6FDE8D688719',
		'9D04486F-08D0-40D3-95AE-BA1C37331815'
	];
	public columnsToDisplay = [
		'selecto-checko',
		'caseName',
		'functionName',
		'duration',
		'documentId'
	];
	public rowHeight$ = this.processingManager.currentRowHeight$;

	constructor(
		public processingManager: ProcessingManager,
		private dateUtility: DateUtility,
		private activatedRoute: ActivatedRoute,
		private notificationManager: NotificationManager
	) {
		super({ itemPrimaryKey: 'stepId' });
		this.refresh();
		this.dataSource$ = combineLatest([
			this.processingManager.snapshot$,
			this.processingManager.currentFilterText$,
			this.skip$,
			this.take$
		]).pipe(
			filter(([collection]) => !!collection),
			map(([logCollection, term, skip, take]) => {
				let finalResults = cloneDeep(logCollection);
				if (term) {
					finalResults = logCollection?.filter((item) => {
						return item.caseName?.toLowerCase().includes(term.toLowerCase()) ||
							item.functionName?.toLowerCase().includes(term.toLowerCase());
					});
				}
				this.total = finalResults.length;
				finalResults = finalResults.slice(skip).slice(0, take);
				this.functionStepMap = new Map<string, string>(finalResults.map((r) => {
					return [r.id, r.functionId];
				}));
				return new MatTableDataSource(finalResults);
			})
		);

		this.activatedRoute.params.pipe(
			map((params) => params?.id)
		).subscribe((x) => {
			this.processingManager.setCurrentActiveStepId(x);
		});

	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.processingManager.setCurrentActiveStepId(null);
	}

	public trackByFn(index: number, item: ProcessingStep) {
		return `${ item.id }`;
	}

	public refresh(): void {
		this.processingManager.refreshSnapshot();
	}

	public calculateDuration(time: string): string {
		return this.dateUtility.relative(time);
	}

	public isFunctionAllowedToFail(): boolean {
		return this.selectedIds.every((x) => {
			const functionId = this.functionStepMap.get(x);
			return this.allowedToFailIds.includes(functionId);
		}) && this.selectedIds.length < 1;
	}

	public retry(): void {
		const operations = this.selectedIds.map((id) => {
			return this.processingManager.retryStep(id);
		});

		forkJoin(operations).subscribe(() => {
			this.notificationManager.displaySnackBarMessage({
				message: `Attempting to retry ${ operations.length } steps.`
			});
			this.selectedIds = [];
		});
	}

	public fail(): void {
		const operations = this.selectedIds.map((id) => {
			return this.processingManager.failStep(id);
		});

		forkJoin(operations).subscribe(() => {
			this.notificationManager.displaySnackBarMessage({
				message: `Attempting to fail ${ operations.length } steps.`
			});
		});

		this.selectedIds = [];
	}

	public movePage(event): void {
		this.skip$.next(event.pageIndex * event.pageSize);
		this.take$.next(event.pageSize);
	}
}
